import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCoins,
  faSync,
  faCrown,
  faCheckCircle,
  faGear,
} from "@fortawesome/free-solid-svg-icons";
import { usePlans } from "../contexts/PlansContext";
import {
  TRAVELER_TIERS,
  BUSINESS_TIERS,
  getTierByKey,
  formatTierPrice,
  AUDIENCE,
} from "../constants/plans";

const ACTIVE = new Set(["active", "trialing"]);

function formatDate(ms) {
  if (!ms) return null;
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (_) {
    return null;
  }
}

/**
 * PlansManager — Stripe Billing membership UI.
 *
 * Existing subscriptions (upgrade / downgrade / cancel / payment method) are
 * managed in the Stripe Customer Portal via a single "Manage subscription"
 * button. "Add a plan" starts Stripe Checkout for an audience the member does
 * not yet have a subscription in.
 */
function PlansManager() {
  const {
    loading,
    membershipSubs = {},
    sevenDayPassExpiresMs,
    isBusinessOwner,
    isBusinessMember,
    startCheckout,
    openBillingPortal,
    claimFreeBusinessTier,
    publicCheckoutDisabled,
  } = usePlans() || {};

  const location = useLocation();
  const [redirecting, setRedirecting] = useState(null); // tierKey | "portal" | null

  const params = new URLSearchParams(location.search || "");
  const checkoutFlag = params.get("checkout"); // "success" | "cancel" | null

  const subs = Object.entries(membershipSubs)
    .map(([id, s]) => ({ id, ...(s || {}) }))
    .filter((s) => ACTIVE.has(s.status));

  const hasTravelerSub = subs.some(
    (s) => getTierByKey(s.tierKey)?.audience === AUDIENCE.TRAVELER,
  );
  const hasBusinessSub = subs.some(
    (s) => getTierByKey(s.tierKey)?.audience === AUDIENCE.BUSINESS,
  );

  const passActive = sevenDayPassExpiresMs && sevenDayPassExpiresMs > Date.now();
  const hasBusinessOwnerPlan =
    typeof isBusinessOwner === "function" && isBusinessOwner();
  const hasAnything = subs.length > 0 || passActive;

  const handleManage = async () => {
    if (redirecting) return;
    setRedirecting("portal");
    try {
      await openBillingPortal();
    } catch (e) {
      console.error("Portal failed:", e);
      window.alert(
        e?.message || "Couldn't open the billing portal. Please try again.",
      );
      setRedirecting(null);
    }
  };

  const handleSubscribe = async (tier) => {
    if (redirecting) return;
    setRedirecting(tier.key);
    try {
      // A $0 tier has no Stripe price — claim it in place instead of Checkout.
      if (!tier.priceUsd) {
        await claimFreeBusinessTier();
        setRedirecting(null);
        return;
      }
      if (publicCheckoutDisabled) {
        window.alert("Subscriptions are temporarily unavailable.");
        setRedirecting(null);
        return;
      }
      await startCheckout(tier.key);
    } catch (e) {
      console.error("Checkout failed:", e);
      window.alert(e?.message || "Couldn't start checkout. Please try again.");
      setRedirecting(null);
    }
  };

  // Only offer tiers for audiences the member has no subscription in — changes
  // within an owned audience go through the portal. The free Crew Listing is
  // offered only to someone with no business tier at all, so a business can
  // opt into the free level without paying.
  const available = [
    ...(hasTravelerSub ? [] : TRAVELER_TIERS.filter((t) => t.priceUsd > 0)),
    ...(hasBusinessSub
      ? []
      : BUSINESS_TIERS.filter(
          (t) => t.priceUsd > 0 || (!isBusinessMember?.() && !t.priceUsd),
        )),
  ];

  return (
    <div className="subscriptions">
      <h3>Your Pirate Plans</h3>

      {checkoutFlag === "success" && (
        <div
          style={{
            marginTop: "10px",
            padding: "10px 12px",
            backgroundColor: "rgba(79, 122, 74, 0.12)",
            border: "1px solid var(--status-success, #4f7a4a)",
            borderRadius: "6px",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <FontAwesomeIcon
            icon={faCheckCircle}
            style={{ color: "var(--status-success, #4f7a4a)" }}
          />
          <span>
            Payment received — your membership will appear here within a few
            seconds.
          </span>
        </div>
      )}
      {checkoutFlag === "cancel" && (
        <div
          style={{
            marginTop: "10px",
            padding: "10px 12px",
            backgroundColor: "rgba(184, 137, 63, 0.12)",
            border: "1px solid var(--brand-brass, #b8893f)",
            borderRadius: "6px",
            fontSize: "14px",
          }}
        >
          Checkout was cancelled — no charge was made.
        </div>
      )}

      {/* Active subscriptions + portal management */}
      {loading ? (
        <div
          style={{
            padding: "15px 0",
            color: "var(--ink-muted)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <FontAwesomeIcon icon={faSync} spin />
          <span>Loading your membership…</span>
        </div>
      ) : hasAnything ? (
        <div>
          <h4 style={{ marginTop: "20px", marginBottom: "10px" }}>
            Your Active Membership
          </h4>
          <ul className="subscription-list">
            {subs.map((sub, index) => {
              const tier = getTierByKey(sub.tierKey);
              const renews = formatDate(sub.currentPeriodEndMs);
              return (
                <li
                  key={sub.id}
                  className="subscription-item"
                  style={{
                    marginBottom: "15px",
                    paddingBottom: "15px",
                    borderBottom:
                      index < subs.length - 1
                        ? "1px solid var(--border-soft, rgba(0,0,0,0.08))"
                        : "none",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start" }}>
                    <FontAwesomeIcon
                      icon={faCrown}
                      style={{
                        marginTop: "3px",
                        marginRight: "10px",
                        color: "var(--brand-brass)",
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                        {tier ? tier.name : "Membership"}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "15px",
                          fontSize: "13px",
                        }}
                      >
                        <span>
                          <span style={{ fontWeight: 600 }}>Status:</span>{" "}
                          {sub.status === "trialing" ? "Trial" : "Active"}
                        </span>
                        {renews &&
                          (sub.cancelAtPeriodEnd ? (
                            <span style={{ color: "var(--brand-brass)" }}>
                              Cancels on {renews}
                            </span>
                          ) : (
                            <span>
                              <span style={{ fontWeight: 600 }}>Renews:</span>{" "}
                              {renews}
                            </span>
                          ))}
                        {tier && (
                          <span>
                            <span style={{ fontWeight: 600 }}>Price:</span>{" "}
                            {formatTierPrice(tier)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {passActive && (
            <div
              style={{
                marginBottom: "12px",
                fontSize: "13px",
                color: "var(--ink-muted)",
              }}
            >
              <FontAwesomeIcon
                icon={faCoins}
                style={{ color: "var(--brand-brass)", marginRight: "6px" }}
              />
              7-Day Adventurer Pass active until{" "}
              {formatDate(sevenDayPassExpiresMs)}.
            </div>
          )}

          {subs.length > 0 && (
            <button
              type="button"
              onClick={handleManage}
              disabled={redirecting === "portal" || publicCheckoutDisabled}
              title={publicCheckoutDisabled ? "Subscription management is temporarily unavailable." : undefined}
              style={{
                background: "var(--brand-navy)",
                color: "var(--ink-inverse, #fff)",
                border: "none",
                padding: "10px 16px",
                borderRadius: "6px",
                cursor: redirecting ? "wait" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
              }}
            >
              <FontAwesomeIcon icon={faGear} />
              {publicCheckoutDisabled
                ? "Coming Soon"
                : redirecting === "portal"
                ? "Opening…"
                : "Manage subscription"}
            </button>
          )}
          <p
            style={{
              margin: "8px 0 0",
              fontSize: "12px",
              color: "var(--ink-muted)",
            }}
          >
            Upgrade, downgrade, change your payment method, or cancel from the
            secure billing portal. Upgrades are prorated and take effect
            immediately; downgrades take effect at the end of your billing
            period.
          </p>

          {hasBusinessOwnerPlan && (
            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                border: "1px solid var(--rope-border, rgba(0,0,0,0.12))",
                borderRadius: "8px",
                background: "var(--surface-parchment-soft)",
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <strong style={{ width: "100%" }}>Business Owner Shortcuts</strong>
              <Link to="/account/business">Open Business Profile</Link>
              <Link to="/account/analytics">Open Business Analytics</Link>
              <Link to="/business-dashboard">Open Business Dashboard</Link>
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: "15px 0", color: "var(--ink-muted)" }}>
          <p>
            No active membership yet. Join a crew below to unlock premium
            features!
          </p>
        </div>
      )}

      {/* Available plans — only audiences the member has no subscription in. */}
      {available.length > 0 && (
        <div>
          <h4 style={{ marginTop: "30px", marginBottom: "4px" }}>
            {hasAnything ? "Add a Plan" : "Available Plans"}
          </h4>
          <p
            style={{
              margin: "0 0 10px",
              fontSize: "13px",
              color: "var(--ink-muted)",
            }}
          >
            You'll be taken to secure Stripe checkout, then back here.
          </p>
          <ul className="subscription-list">
            {available.map((tier, index) => {
              const busy = redirecting === tier.key;
              return (
                <li
                  key={tier.key}
                  className="subscription-item"
                  style={{
                    marginBottom: "15px",
                    paddingBottom: "15px",
                    borderBottom:
                      index < available.length - 1
                        ? "1px solid var(--border-soft, rgba(0,0,0,0.08))"
                        : "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faCoins}
                      style={{ marginTop: "3px", color: "var(--brand-brass)" }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                        {tier.name}
                      </div>
                      {tier.tagline && (
                        <div
                          style={{
                            fontSize: "14px",
                            color: "var(--ink-muted)",
                            marginBottom: "5px",
                          }}
                        >
                          {tier.tagline}
                        </div>
                      )}
                      <div style={{ fontSize: "13px" }}>
                        <span style={{ fontWeight: "600" }}>Price:</span>{" "}
                        {formatTierPrice(tier)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSubscribe(tier)}
                      disabled={
                        busy ||
                        (publicCheckoutDisabled && !!tier.priceUsd) ||
                        typeof (tier.priceUsd ? startCheckout : claimFreeBusinessTier) !==
                          "function"
                      }
                      style={{
                        alignSelf: "center",
                        background: "var(--brand-brass)",
                        color: "#fff",
                        border: "none",
                        padding: "8px 14px",
                        borderRadius: "4px",
                        cursor: busy ? "wait" : "pointer",
                        whiteSpace: "nowrap",
                        fontSize: "13px",
                      }}
                    >
                      {busy
                        ? (tier.priceUsd ? "Redirecting…" : "Setting up…")
                        : !tier.priceUsd
                        ? "List Free"
                        : publicCheckoutDisabled
                        ? "Coming Soon"
                        : tier.billing === "ONE_TIME"
                        ? "Get Pass"
                        : "Subscribe"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default PlansManager;
